import { type UseComboboxState, type UseComboboxStateChangeOptions, useCombobox } from 'downshift';
import { useState } from 'react';

import { cn } from '~/styles/extended-theme.ts';

type ComboboxSelectItem = string;

interface Props {
  name: string;
  items: ComboboxSelectItem[];
  value: string;
  onChange: (value: string) => void;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  containerClassName?: string;
}

const stateReducer = (
  _state: UseComboboxState<ComboboxSelectItem>,
  actionAndChanges: UseComboboxStateChangeOptions<ComboboxSelectItem>,
) => {
  const { type, changes } = actionAndChanges;

  switch (type) {
    case useCombobox.stateChangeTypes.InputClick:
      return { ...changes, isOpen: false };

    case useCombobox.stateChangeTypes.InputChange:
      return {
        ...changes,
        isOpen: changes.inputValue !== undefined && changes.inputValue.length >= 3,
      };

    default:
      return changes;
  }
};

export function CityCombobox({
  value,
  name,
  items: allItems,
  inputProps,
  onChange,
  containerClassName,
}: Props) {
  const [items, setItems] = useState(allItems);
  const { className: inputClassName, ...otherInputProps } = inputProps ?? {};

  const cb = useCombobox<ComboboxSelectItem>({
    items,
    stateReducer,
    inputValue: value,
    onInputValueChange: ({ inputValue }) => {
      onChange(inputValue ?? '');
      setItems(
        allItems.filter(
          (item) =>
            inputValue === undefined || item.toLowerCase().startsWith(inputValue.toLowerCase()),
        ),
      );
    },
  });

  return (
    <div className={containerClassName}>
      <input
        {...cb.getInputProps({
          ...otherInputProps,
          name,
          type: 'text',
          className: cn('bg-transparent font-light', inputClassName),
          placeholder: 'City or zip code',
          onKeyDown: (e) => {
            if (e.key === 'Enter' && cb.highlightedIndex === -1) {
              // submits the form if the user presses enter and there's no highlighted item
              // see https://github.com/downshift-js/downshift#event-handlers and https://github.com/downshift-js/downshift/issues/734
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (e.nativeEvent as any).preventDownshiftDefault = true;
            }
          },
        })}
      />

      <ul
        className={`absolute z-10 mt-1 max-h-80 w-72 overflow-y-auto rounded-md border border-solid bg-white p-2 shadow-md ${
          !(cb.isOpen && items.length) && 'hidden'
        }`}
        {...cb.getMenuProps()}
      >
        {cb.isOpen
          ? items.map((item, index) => (
              <li
                key={item}
                className={cn('cursor-pointer py-1 text-primary', {
                  'font-bold': cb.highlightedIndex === index,
                })}
                {...cb.getItemProps({ item, index })}
              >
                {item}
              </li>
            ))
          : null}
      </ul>
    </div>
  );
}
